import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import { getRichTextFieldContent } from '@/component/ticketList/handler/TicketHandler';
import {
    getProjectUserInUseDetail,
    updateAISettingForProjectSetting
} from '@/service/projectService';
import { getTicketsKnowledgePrompt } from '@/service/ticketService';
import { getPublicAPI } from '@/service/publicService';

/**
 * initTicketKnowledgePrompt
 * @param {*} API
 * @param {*} corporationId
 * @param {*} projectId
 * @returns
 */
export const initTicketKnowledgePrompt = async (API, corporationId, projectId, corporationInUseId) => {
    if(projectId === Constant.KNOWLEDGE_VIEW_OPTION.ALL_KNOWLEDGE_IN_ORG) {
        corporationId = corporationInUseId;
    }

    if (!API || Utils.isEmptyString(corporationId) || Utils.isEmptyString(projectId)) return [];

    let paramsData = {
        corporationId,
        projectId
    };

    let res = await getTicketsKnowledgePrompt(API, paramsData);

    if (!res || !res.status) return [];

    return JSON.parse(res.result);
};

export const initDataDefaults = ({ data, setListInternal, setListExternal, isManager, isMember }) => {
    const listInternal =
        data?.listPrompt
            ?.filter(item =>{
                const settingAIPromptApplicationTarget = item?.settingAI?.AIPromptApplicationTarget
                if(Utils.isEmptyArray(settingAIPromptApplicationTarget)) return false

                return settingAIPromptApplicationTarget.includes(
                    Constant.AI_SYSTEM_FIELD_TARGET_OPTION_TAGS.INTERNAL
                )}
            )
            ?.sort((a, b) => a.title.localeCompare(b.title)) || [];

    const listExternal =
        data?.listPrompt
            ?.filter(item =>{
                const settingAIPromptApplicationTarget = item?.settingAI?.AIPromptApplicationTarget
                if(Utils.isEmptyArray(settingAIPromptApplicationTarget)) return false

                return settingAIPromptApplicationTarget.includes(
                    Constant.AI_SYSTEM_FIELD_TARGET_OPTION_TAGS.EXTERNAL
                )}
            )
            ?.sort((a, b) => a.title.localeCompare(b.title)) || [];

    if (!isManager && !isMember) {
        setListInternal([]);
        setListExternal(listExternal);
        return;
    }

    setListInternal(listInternal);
    setListExternal(listExternal);
};

const updateProjectForPromptKnowledge = async (API, projectId, type, inputSetting, data) => {
    let paramsData = {
        projectId,
        type,
        inputSetting,
        data: JSON.stringify(data)
    };

    let res = await updateAISettingForProjectSetting(API, paramsData);

    if (!res || !res.status) return [];

    return JSON.parse(res.result);
};

export const toggleBtnSwitch = async (
    API,
    projectId,
    inputSetting,
    type,
    toggleBtn,
    setToggle,
    setIsLoading,
    setUsedProject
) => {
    setIsLoading(true);
    setToggle(toggleBtn);
    let data = {
        [type]: toggleBtn
    };
    const updateBtn = await updateProjectForPromptKnowledge(
        API,
        projectId,
        type,
        inputSetting,
        data
    );

    setUsedProject({ ...updateBtn });
    // setIsLoading(false);
};

export const handleOnBlurBasicSetting = async (
    e,
    API,
    projectId,
    inputSetting,
    type,
    setIsLoading,
    setUsedProject,
    setErrorBasicPrompt,
    setTriggerReload,
    setValueBasicPrompt
) => {
    setIsLoading(true);

    if (Utils.isEmptyString(e.target.value)) {
        setErrorBasicPrompt(prev => ({
            ...prev,
            [inputSetting]: ''
        }));
        setIsLoading(false);
        return;
    }

    if (!Utils.isValidLinkPromptInput(e.target.value)) {
        setErrorBasicPrompt(prev => ({
            ...prev,
            [inputSetting]: Constant.AI_PROMPT_VALID_CODE.MSG_CM_INVALID_LINK
        }));
        setIsLoading(false);
        return;
    }

    const promptAIAppTarget =
        inputSetting === Constant.AI_PROMPT_SCOPED.INTERNAL_PRJ
            ? Constant.AI_SYSTEM_FIELD_TARGET_OPTION_TAGS.INTERNAL
            : Constant.AI_SYSTEM_FIELD_TARGET_OPTION_TAGS.EXTERNAL;

    let linkInput = e.target.value.trim();

    if (Utils.isShortShareLink(linkInput)) {
        const result = await getPublicAPI(null, {
            getApiType: Constant.GET_API_PUBLIC_TYPE.GET_DATA_SHARE_LINK_BY_SHORT_KEY,
            shortKey: linkInput.slice(-7)
        });
        const dataShareLink = Utils.convertJSONDataToObject(result.payload);
        if (!Utils.isEmptyObject(dataShareLink)) {
            linkInput = `${window.location.origin}/${Utils.getUrlLinkShareLinkTicket(
                dataShareLink.generateLinkCorporationDomain,
                dataShareLink.generateLinkProjectId,
                dataShareLink.targetId
            )}`;
        }
    }

    let data = {
        [type]: {
            promptAIAppTarget,
            linkInput
        }
    };

    const updateBasic = await updateProjectForPromptKnowledge(
        API,
        projectId,
        type,
        inputSetting,
        data
    );

    if (updateBasic?.error) {
        setErrorBasicPrompt(prev => ({ ...prev, [inputSetting]: updateBasic?.error }));
        return setIsLoading(false);
    }
    setValueBasicPrompt(prev => ({
        ...prev,
        [inputSetting]: e.target.value || ''
    }));
    setUsedProject({ ...updateBasic });
    setErrorBasicPrompt(prev => ({
        ...prev,
        [inputSetting]: ''
    }));
    setTriggerReload(Date.now().toString());
};

export const handleDeleteBasicPrompt = async (
    API,
    projectId,
    inputSetting,
    type,
    setIsLoading,
    setConfirmDeleteBasicPrompt,
    setUsedProject
) => {
    setConfirmDeleteBasicPrompt(false);
    setIsLoading(true);

    const deleteUpdate = await updateProjectForPromptKnowledge(
        API,
        projectId,
        type,
        inputSetting,
        {}
    );

    if (deleteUpdate) {
        setUsedProject({ ...deleteUpdate });
    }

    // setIsLoading(false);
};

export const checkUserRoleInProject = async (
    API,
    projectInuseDetail,
    userDetail,
) => {
    let projectDetail = await getProjectUserInUseDetail(API, userDetail.id, projectInuseDetail.id);
    if (!projectDetail) {
        return {
            isManager: false,
            isMember: false
        }
    }
    const isManager = 
        !Utils.isEmptyObject(projectDetail) &&
        projectDetail.privilege === Constant.PRIVILEGE_PROJECT.PRJ_MGR;

    const isMember = Utils.isEmptyObject(projectDetail) || projectDetail?.privilege !== Constant.PRIVILEGE_PROJECT.PRJ_MGR;

    return {isManager, isMember};
};

export const handleDisplayContentAIBody = (promptBodyParams, isDisplayBreakLine = false, isDisplayForAIChatInput = false) => {
    if (Utils.isEmptyString(promptBodyParams)) return '';
    let promptBody = ""
    try {
        const doc = JSON.parse(promptBodyParams);
        promptBody = doc
            .replace(/<\/p>/gi, '\n')
            .replace(/<\/p>/gi, '\n')
            .replace(/<\/li>/gi, '\n') // Thay thế thẻ đóng </p> bằng ký tự xuống dòng
            .replace(/<\/?h[1-6]\s*\/?>/gi, '\n') // heading tag
            .replace(/<br\s*\/?>/gi, '\n') // br tag
            .replace(/<[^>]+>/gi, '') // Loại bỏ tất cả các thẻ HTML khác
            // .replace(/&nbsp;/g, ' ') // Thay thế &nbsp; bằng khoảng trắng
            .trim(); // Loại bỏ khoảng trắng ở đầu và cuối
        promptBody = promptBody.replace(/\n/g, '<br>');
        return promptBody;
    } catch (error) {
        promptBody = '';
    }
    // let regex =  /\\n|\n|<\/br>/g
    
    // const parser = new DOMParser();
    // const doc = parser.parseFromString(promptBody, 'text/html');

    // const imgElements = doc.querySelectorAll('img');
    // imgElements.forEach((imgElement) => {
    //     imgElement.parentNode.removeChild(imgElement);
    // });

    // console.log('isDisplayBreakLine :>> ', isDisplayBreakLine);
    // if(isDisplayBreakLine) regex = /^$/g

    // const sanitizedHtmlContent = doc.body.innerHTML.replace(regex, '').trim();

    // if(isDisplayForAIChatInput) {
    //     const richTextContent = getRichTextFieldContent(sanitizedHtmlContent, true);
    //     return richTextContent.replace(/<\/br>\s*/g,"\n") 
    // }
    // return getRichTextFieldContent(sanitizedHtmlContent, true);
}

/**
 * handleGetAIBodyStringContent
 * get string content of AI body when click used basic prompt
 * @param {*} promptBodyParams 
 * @returns  string
 */
export const handleGetAIBodyStringContent = (promptBodyParams) => {
    if (Utils.isEmptyString(promptBodyParams)) return '';
    const doc = JSON.parse(promptBodyParams);
    let promptBody = doc.replace(/<\/p>/gi, '\n')
    .replace(/<\/p>/gi, '\n') 
    .replace(/<\/li>/gi, '\n')   // Thay thế thẻ đóng </p> bằng ký tự xuống dòng
    .replace(/<\/?h[1-6]\s*\/?>/gi, '\n') // heading tag
    .replace(/<br\s*\/?>/gi, '\n')  // br tag
    .replace(/<[^>]+>/gi, '') // Loại bỏ tất cả các thẻ HTML khác
    .replace(/&nbsp;/g, ' ') // Thay thế &nbsp; bằng khoảng trắng
    .trim(); // Loại bỏ khoảng trắng ở đầu và cuối
    promptBody = promptBody.replace(/\n/g, '<br>');
    return promptBody
}